/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { createRef, Ref, ref } from 'lit/directives/ref.js';

import { iconX, iconZoomIn } from '@/components/icons';
import { BREAKPOINT_MEDIUM_PX } from '@/settings';
import { baseStyles } from '@/styles';

import '@/components/image/fg-image';

@customElement('fg-fullscreen-image')
export class FgFullscreenImage extends LitElement {
  private dialogRef: Ref<HTMLDialogElement> = createRef();
  private closeButton: Ref<HTMLButtonElement> = createRef();

  @property({ type: String })
  imageUrl?: string | null;

  @property({ type: Boolean })
  hasZoomInCursor: boolean = false;

  public open(event: Event) {
    event.stopPropagation();
    const dialogRef = this.dialogRef;
    const closeButton = this.closeButton;

    dialogRef.value?.showModal();
    closeButton.value?.focus();

    this.lockOrUnlockBody(true);

    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.closeModal(e);
      }
    });
  }

  lockOrUnlockBody = (lock: boolean) => {
    const body = document.querySelector('body');
    if (!body) return;
    body.style.overflowY = lock ? 'hidden' : '';
  };

  closeModal = (event: Event) => {
    event.stopPropagation();
    const ref = this.dialogRef;
    ref?.value?.close();
    this.lockOrUnlockBody(false);
  };

  render() {
    const dialogRefTemplate = ref(this.dialogRef);
    const closeButtonTemplate = ref(this.closeButton);

    if (!this.imageUrl || this.imageUrl === '') {
      return nothing;
    }

    return html`
      <button
        class="${classMap({
          'zoom-in-cursor': this.hasZoomInCursor,
        })} zoom-in-button"
        @click=${this.open}
      >
        ${iconZoomIn}
      </button>
      <dialog ${dialogRefTemplate} @click=${this.closeModal}>
        <button
          class="close-button"
          ${closeButtonTemplate}
          @click=${this.closeModal}
        >
          ${iconX}
        </button>
        <div class="image-wrapper">
          <fg-image height="600" src="${this.imageUrl}" width="600"></fg-image>
        </div>
      </dialog>
    `;
  }

  static styles = [
    baseStyles,
    css`
      body:has(dialog[open]) {
        overflow: hidden;
      }

      dialog {
        border-radius: var(
          --fg-borderradius--lg,
          var(--fg-fallback-borderradius--lg)
        );
        border: none;
      }
      .zoom-in-cursor {
        cursor: zoom-in;
      }
      .zoom-in-button {
        width: 24px;
        height: 24px;
        background-color: #ffffff;
        box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: var(
          --fg-borderradius--xs,
          var(--fg-fallback-borderradius--xs)
        );
        flex-shrink: 0;
      }

      .image-wrapper {
        width: 250px;
        height: 250px;
        max-width: 100vw;
        max-height: 100vw;
        margin: 0.313rem;
        @media screen and (min-width: ${BREAKPOINT_MEDIUM_PX}px) {
          && {
            width: 50vw;
            height: 50vw;
            max-width: 600px;
            max-height: 600px;
          }
        }
      }

      .image-wrapper fg-image::part(image) {
        aspect-ratio: 1;
        background-color: #ffffff;
        display: block;
        height: auto;
        object-fit: contain;
        width: 100%;
      }

      .zoom-in-button svg {
        width: 16px;
        height: 16px;
      }

      .close-button {
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #ffffff;
        border-radius: var(--fg-borderradius, var(--fg-fallback-borderradius));
        position: fixed;
        top: 16px;
        right: 16px;
        padding: 0.375rem;
      }

      dialog::backdrop {
        background-color: rgb(0 0 0 / 0.5);
        backdrop-filter: blur(16px);
      }
    `,
  ];
}
